import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, css, styled, Typography } from '@mui/material'
import type { ParseKeys } from 'i18next'
import type { FC } from 'react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, ICONS, LinkUnstyled, remUnit, unit } from '@hcr/ui'

import { LINK_CONFIG } from '../../../../../../configs'
import { Path } from '../../../../../../models/navigation'

interface NavigationLinksProps {
  isLogged: boolean
  isOwner: boolean
}

interface NavLinkConfig {
  isActive?: boolean
  key: string
  labelKey: ParseKeys
  showIcon?: boolean
  to: string
  variant: 'bodyM' | 'labelM'
  visible?: boolean
  withDivider?: boolean
}

export const NavigationLinks: FC<NavigationLinksProps> = ({ isLogged, isOwner }) => {
  const links: NavLinkConfig[] = [
    { key: 'webshop', to: LINK_CONFIG.webshop, labelKey: 'account.holidayclub.fi', variant: 'bodyM', showIcon: true },
    {
      key: 'home',
      to: Path.Home,
      labelKey: 'account.omat-lomasivut',
      variant: 'bodyM',
      showIcon: true,
      isActive: true,
    },
    { key: 'booking', to: Path.Booking, labelKey: 'booking.your-booking', variant: 'labelM' },
    {
      key: 'owner',
      to: Path.Owner,
      labelKey: 'owner.your-holdings',
      variant: 'labelM',
      withDivider: true,
      visible: !isLogged || isOwner,
    },
    {
      key: 'account',
      to: Path.Account,
      labelKey: 'account.personal-information',
      variant: 'labelM',
      withDivider: true,
      visible: isLogged,
    },
  ]

  return (
    <Box component='nav'>
      <LinksList component='ul'>
        {links
          .filter(link => link.visible ?? true)
          .map(link => (
            <Fragment key={link.key}>
              {link.withDivider && <Divider />}
              <NavigationLink {...link} />
            </Fragment>
          ))}
      </LinksList>
    </Box>
  )
}

const NavigationLink: FC<NavLinkConfig> = ({ to, labelKey, variant, isActive, showIcon }) => {
  const { t } = useTranslation()
  const LinkComponent = isActive ? ActiveLink : Link

  return (
    <LinkComponent component={LinkUnstyled} to={to} aria-current={isActive ? 'page' : undefined}>
      <Typography
        variant={variant}
        fontWeight={variant === 'labelM' ? 'normal' : undefined}
        noWrap
        sx={showIcon ? { flex: 1, minWidth: 0 } : { width: '100%' }}
      >
        {t(labelKey)}
      </Typography>
      {showIcon && (
        <IconBox>
          <Icon aria-hidden='true' icon={ICONS.fasChevronRight} />
        </IconBox>
      )}
    </LinkComponent>
  )
}

const LinksList = styled(Box)`
  & .MuiButton-root:hover {
    background-color: inherit;
    box-shadow: none;
  }
` as typeof Box

const IconBox = styled(Box)`
  height: ${unit(11)};
  width: ${unit(11)};
  display: flex;
  justify-content: center;
  align-items: center;
`

const Icon = styled(FontAwesomeIcon)`
  text-align: center;
  font-size: ${remUnit(4.5)};
  line-height: ${remUnit(4)};
`
const linkProps = css`
  display: flex;
  justify-content: space-between;
  padding: ${unit(2)} ${unit(3)} ${unit(2.5)} ${unit(5.5)};
  color: ${COLORS.black};
  height: ${unit(15.25)};
  text-align: left;

  &:hover {
    color: ${COLORS.red['dark']};
  }
`

const Link = styled(Button)`
  ${linkProps};
` as typeof Button

const ActiveLink = styled(Button)`
  ${linkProps};
  border-left: ${unit(1)} solid ${COLORS.burgundi[500]};
  border-bottom: ${unit(0.25)} solid ${COLORS.vaalea[600]};
  border-top: ${unit(0.25)} solid ${COLORS.vaalea[600]};
` as typeof Button

const Divider = styled(Box)`
  height: 0;
  border-bottom: ${unit(0.25)} solid ${COLORS.vaalea[600]};
  border-bottom-style: dashed;
  margin: 0 ${unit(5)};
`
